import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MapPin, Calendar, Clock, ArrowRight } from 'lucide-react';

const BlogList = ({ spots, showTitle = true }) => {
  if (!spots || spots.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {showTitle && (
          <div className="text-center mb-12">
            <div className="bg-white/85 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 mx-auto max-w-4xl">
              <h1 className="text-4xl font-bold text-gray-800 mb-4">
                Découvrez nos Spots Panoramiques
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Guides détaillés sur les plus beaux points de vue de France. 
                Guides complets avec photos, accès et conseils pratiques.
              </p>
            </div>
          </div>
        )}
        
        <div className="text-center py-16">
          <div className="max-w-md mx-auto">
            {/* Container avec fond contrasté pour la lisibilité jour/nuit */}
            <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 p-8">
              <div className="text-6xl mb-6">🌅</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                De magnifiques spots arrivent bientôt !
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Nous préparons avec soin une sélection des plus beaux points de vue panoramiques de France. 
                Chaque spot sera accompagné de guides détaillés, photos haute résolution et informations pratiques.
              </p>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6 shadow-sm">
                <h3 className="font-semibold text-amber-800 mb-2 flex items-center justify-center">
                  🔄 En préparation
                </h3>
                <p className="text-amber-700 text-sm leading-relaxed">
                  Nos équipes explorent actuellement de nouveaux spots exceptionnels à partager avec vous. 
                  Restez connectés pour découvrir ces pépites panoramiques !
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {showTitle && (
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-amber-900 mb-4">
            Découvrez nos Spots Panoramiques
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Guides détaillés sur les plus beaux points de vue de France. 
            Guides complets avec photos, accès et conseils pratiques.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {spots.map((spot) => (
          <article key={spot.id} className="group">
            <Card className="h-full hover:shadow-lg transition-all duration-300 overflow-hidden">
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={spot.images[0]?.src || '/images/spots/default-spot.jpg'}
                  alt={spot.images[0]?.alt || spot.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  width={400}
                  height={300}
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-white/90 text-amber-800 border-0">
                    {spot.difficulty}
                  </Badge>
                </div>
              </div>

              <CardHeader className="pb-3">
                <CardTitle className="text-xl line-clamp-2 group-hover:text-amber-700 transition-colors">
                  <Link to={`/blog/spots/${spot.slug}`}>
                    {spot.title}
                  </Link>
                </CardTitle>
                <CardDescription className="flex items-center text-muted-foreground">
                  <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                  <span className="truncate">{spot.location}</span>
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-0">
                {/* Description */}
                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                  {spot.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {spot.tags.slice(0, 3).map((tag, index) => (
                    <Badge key={index} variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                      {tag}
                    </Badge>
                  ))}
                  {spot.tags.length > 3 && (
                    <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600">
                      +{spot.tags.length - 3}
                    </Badge>
                  )}
                </div>

                {/* Meta info */}
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                  <div className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    {new Date(spot.publishedAt).toLocaleDateString('fr-FR')}
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    {spot.accessTime}
                  </div>
                </div>

                {/* CTA */}
                <Button 
                  asChild 
                  variant="outline" 
                  className="w-full group/btn hover:bg-amber-50 hover:border-amber-300"
                >
                  <Link to={`/blog/spots/${spot.slug}`}>
                    Lire le guide
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </article>
        ))}
      </div>
    </section>
  );
};

export default BlogList;